import { HrefListItem as HrefListItemType } from '@/schemas/sceneDataSchema';
import { Box, Icon, styled, Typography } from '@mui/material';
import { TypographyColors } from '@/constants/typographyTypes';
import { CircularProgress } from '@/components/CircularProgress.tsx';
import { CircularProgressSize } from '@/constants/circularProgressTypes.tsx';
import { ChipColors, ChipSizes, ChipVariant } from '@/constants/chipTypes';
import { InertChip } from '@/components/DynamicUI/InertChip';

interface HrefListItemProps {
  component: HrefListItemType;
}

export const HrefListItem: React.FC<HrefListItemProps> = ({
  component: {
    icon,
    overline,
    label1,
    label2,
    supporting_text,
    with_dividers,
    icon_right,
    href,
    is_loading,
    trailing_pill,
  },
}) => {
  const handleClick = () => {
    if (!is_loading && href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <StyledListItem
      withDividers={!!with_dividers}
      isLoading={!!is_loading}
      onClick={handleClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.primary' }}>
        {icon && <Icon>{icon}</Icon>}
        <TextGroup>
          {overline && (
            <Typography
              variant='overline'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {overline}
            </Typography>
          )}
          <Typography variant='body1'>{label1}</Typography>
          {label2 && <Typography variant='body1'>{label2}</Typography>}
          {supporting_text && (
            <Typography
              variant='body2'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {supporting_text}
            </Typography>
          )}
        </TextGroup>
      </Box>
      <RightIconContainer>
        {!is_loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {trailing_pill && (
              <InertChip
                label={trailing_pill}
                size={ChipSizes.SMALL}
                color={ChipColors.TEXT}
                variant={ChipVariant.FILLED}
              />
            )}
            {icon_right && (
              <Icon
                className={icon_right === 'chevron_right' ? 'chevron-right' : ''}
                color='secondary'
              >
                {icon_right}
              </Icon>
            )}
          </Box>
        )}
        {is_loading && <CircularProgress size={CircularProgressSize.SMALL}></CircularProgress>}
      </RightIconContainer>
    </StyledListItem>
  );
};

const StyledListItem = styled('div')<{ withDividers?: boolean; isLoading?: boolean }>`
  ${({ isLoading }) => !isLoading && 'cursor: pointer'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 12px;
  /* Handle optional dividers between ListItems */
  ${({ withDividers }) => withDividers && 'border-bottom: 1px solid var(--mui-palette-border-main);'}
  &:last-of-type {
    border-bottom: none;
  }
  /* Animate chevron icon */
  .chevron-right {
    transition: transform 0.2s var(--ease-out-quart);
  }
  &:hover {
    .chevron-right {
      transform: translateX(4px);
    }
  }
`;

const TextGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
  & > span,
  & > p {
    line-height: 1;
  }
`;

const RightIconContainer = styled('span')`
  display: flex;
  align-items: center;
  justify-content: center;
  // Keep container size consistent when changing isLoading
  height: 24px;
  min-height: 24px;
`;
